/**
 * Graphics quality presets.
 *
 * The renderer is happy to draw shadows, bloom and a few hundred props at native
 * resolution, but a phone or an integrated GPU is not. Rather than guess once
 * and be stuck with it, the game picks a sensible default from what the device
 * advertises and then lets the player override it; the choice is remembered.
 *
 * Only renderer-level settings are switched here. Scenery density is baked into
 * the track geometry, which is built once and cached, so changing quality never
 * forces an expensive rebuild.
 */

export type QualityLevel = 'low' | 'medium' | 'high';

export interface QualitySettings {
  /** Cap on devicePixelRatio. The single biggest lever on fill rate. */
  pixelRatio: number;
  shadows: boolean;
  /** Shadow map resolution, ignored when shadows are off. */
  shadowMapSize: number;
  bloom: boolean;
  /** Multisample count for the post-processing target; 0 disables MSAA. */
  samples: number;
  /** Scales how many particles the effects layer emits. */
  particleScale: number;
}

const PRESETS: Record<QualityLevel, QualitySettings> = {
  low: {
    pixelRatio: 1,
    shadows: false,
    shadowMapSize: 1024,
    bloom: false,
    samples: 0,
    particleScale: 0.4,
  },
  medium: {
    pixelRatio: 1.5,
    shadows: true,
    shadowMapSize: 1024,
    bloom: false,
    samples: 0,
    particleScale: 0.7,
  },
  high: {
    pixelRatio: 2,
    shadows: true,
    shadowMapSize: 2048,
    bloom: true,
    samples: 4,
    particleScale: 1,
  },
};

export const QUALITY_LEVELS: QualityLevel[] = ['low', 'medium', 'high'];

export const QUALITY_LABELS: Record<QualityLevel, string> = {
  low: 'Low — smoothest',
  medium: 'Medium',
  high: 'High — prettiest',
};

const STORAGE_KEY = 'wkr-quality';

function isLevel(value: unknown): value is QualityLevel {
  return value === 'low' || value === 'medium' || value === 'high';
}

/**
 * Guess a starting point. A coarse pointer means a phone or tablet, where fill
 * rate is the binding constraint; otherwise lean on core count as a rough proxy
 * for how serious the machine is.
 */
function detectDefault(): QualityLevel {
  const coarse =
    typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
  if (coarse) return 'low';

  const cores = navigator.hardwareConcurrency ?? 4;
  if (cores <= 4) return 'medium';
  return 'high';
}

/** The player's saved choice, or a guess from the device. */
export function loadQuality(): QualityLevel {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (isLevel(saved)) return saved;
  } catch {
    /* storage unavailable; fall through to detection */
  }
  return detectDefault();
}

export function saveQuality(level: QualityLevel): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, level);
  } catch {
    /* storage unavailable; the setting just won't persist */
  }
}

export function settingsFor(level: QualityLevel): QualitySettings {
  return PRESETS[level];
}
